import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Spin, Empty, Typography, Image } from "antd";
import { LeftOutlined, RightOutlined, ZoomInOutlined } from "@ant-design/icons";

import { useApi, ApiNames } from "../../hooks/api";
import { GRAY_500 } from "../../constant/colors";
import { toImageDataUri } from "./screenshotReview";

const { Text } = Typography;

/**
 * "Source EMR screenshots" popup — the images the agent extracted the
 * patient's current EMR data from, shown one at a time with Prev/Next.
 * Fetches on open (PHI-gated endpoint; images arrive base64-inline) and
 * resets to the first image on every re-open. Read-only.
 */
const EmrScreenshotsViewer = ({ patientId, open, onClose }) => {
  const { t } = useTranslation();
  const [fetchScreenshots, result, isFetching, fetchError] = useApi(ApiNames.AgenticEmrScreenshots);
  const [index, setIndex] = useState(0);

  // Fetch when the popup opens (fresh each open — the underlying data can
  // change between reviews). Ref'd invoke: `useApi` returns a fresh identity
  // per render, and effects must not key on it.
  const fetchRef = useRef(fetchScreenshots);
  fetchRef.current = fetchScreenshots;
  useEffect(() => {
    if (open && patientId) {
      setIndex(0);
      fetchRef.current(patientId);
    }
  }, [open, patientId]);

  const screenshots = result?.screenshots ?? [];
  const current = screenshots[Math.min(index, screenshots.length - 1)];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      title={t("agentic_board.consent.screenshots_title")}
      data-testid="agentic-emr-screenshots-viewer"
    >
      {isFetching ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spin data-testid="agentic-emr-screenshots-loading" />
        </div>
      ) : fetchError ? (
        <Text type="danger" data-testid="agentic-emr-screenshots-error">
          {t("agentic_board.consent.screenshots_error")}
        </Text>
      ) : screenshots.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("agentic_board.consent.screenshots_empty")} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
          <Image
            src={toImageDataUri(current)}
            alt={t("agentic_board.consent.screenshots_alt", { index: index + 1 })}
            style={{ maxWidth: "100%", maxHeight: "62vh", objectFit: "contain", border: "1px solid #e4e7ec" }}
            wrapperStyle={{ maxWidth: "100%" }}
            preview={{
              mask: (
                <span>
                  <ZoomInOutlined /> {t("agentic_board.consent.screenshots_zoom_hint")}
                </span>
              )
            }}
            data-testid="agentic-emr-screenshot-image"
          />
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button
              icon={<LeftOutlined />}
              disabled={index === 0}
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              data-testid="agentic-emr-screenshots-prev"
            />
            <Text style={{ color: GRAY_500 }} data-testid="agentic-emr-screenshots-counter">
              {t("agentic_board.consent.screenshots_counter", { current: index + 1, total: screenshots.length })}
            </Text>
            <Button
              icon={<RightOutlined />}
              disabled={index >= screenshots.length - 1}
              onClick={() => setIndex((i) => Math.min(screenshots.length - 1, i + 1))}
              data-testid="agentic-emr-screenshots-next"
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export { EmrScreenshotsViewer };
